export type XlsxCell = string | number | Date | null | undefined;

export interface XlsxColumn {
  header: string;
  /** Width in characters. */
  width?: number;
  /** Excel number format for numeric and date cells, e.g. `#,##0` or `yyyy-mm-dd hh:mm`. */
  format?: string;
}

/**
 * A local date and time as Excel should show it. Excel dates carry no time
 * zone and the writer converts from UTC, so the wall-clock time is passed as
 * UTC — otherwise every time would be shifted by the local offset.
 */
export function xlsxDateTime(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const local = new Date(iso);
  return new Date(
    Date.UTC(local.getFullYear(), local.getMonth(), local.getDate(), local.getHours(), local.getMinutes()),
  );
}

/**
 * Save a one-sheet .xlsx in the browser: a bold, frozen header row, then the
 * rows with numbers and dates kept as real numbers and dates. The writer is
 * loaded on first use so it stays out of the main bundle.
 */
export async function downloadXlsx(
  filename: string,
  sheet: string,
  columns: XlsxColumn[],
  rows: XlsxCell[][],
): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const header = columns.map((column) => ({ value: column.header, fontWeight: 'bold' as const }));
  const body = rows.map((row) =>
    row.map((value, index) => {
      if (value === null || value === undefined || value === '') return null;
      const format = columns[index]?.format;
      if (typeof value === 'number') return { value, type: Number, format };
      if (value instanceof Date) return { value, type: Date, format: format ?? 'yyyy-mm-dd hh:mm' };
      return { value, type: String };
    }),
  );
  await writeXlsxFile([header, ...body], {
    sheet,
    stickyRowsCount: 1,
    columns: columns.map((column) => ({ width: column.width })),
  }).toFile(filename);
}
